// src/modules/app-views/services/pages/Document/tabs/FiscalTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useCallback, useEffect } from 'react';
import { Card, Col, Row, Tag } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSelectAsync from 'components/SimpleComponents/SimpleSelectAsync';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import { getServiceTaxDefaults, getCrtCode } from 'utils/fiscalDefaults';
import { CRT_TAG_CONFIG } from 'services/fiscalService';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServicesFiscalTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');
  const storedSubscription = useZustand('auth', 'subscription');
  const { data, loading } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const lc116Options = useMemo(
    () => buildLc116Options(storedParameters, storedModule?.id),
    [storedParameters, storedModule?.id]
  );

  const taxRegime = storedSubscription?.fiscalData?.taxRegime;
  const crtCode = getCrtCode(taxRegime);
  const crtConfig = CRT_TAG_CONFIG[crtCode];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Pre-fill tax retention defaults for new documents based on company CRT
  useEffect(() => {
    const isNew = !data?._id || data._id === 'add';
    if (!isNew || !form || !taxRegime) return;

    form.setFieldsValue(getServiceTaxDefaults(taxRegime));
  }, [data?._id, form, taxRegime]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCnaeFetch = useCallback(async searchValue => {
    const url = `${API.FISCAL.CNAE_SEARCH}?q=${encodeURIComponent(searchValue)}&limit=20`;
    const [response] = await axiosService.get({ url, returnAsObject: true });
    const items = response?.data || [];

    return items.map(item => ({
      value: item.code,
      label: `${item.codeFormatted || item.code} - ${item.description}`,
    }));
  }, []);

  const handleCnaeFetchByValue = useCallback(async code => {
    const url = API.FISCAL.CNAE_GET.replace(':code', code);
    const [item] = await axiosService.get({ url, returnAsObject: true });

    if (!item || !item.found) return null;

    return {
      value: item.code,
      label: `${item.codeFormatted || item.code} - ${item.description}`,
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="serviceFiscal" isFetching={loading} data={data} />

      {/* FISCAL DATA CARD */}
      <Card title={<div className="text-muted">{translateX('fiscal_data')}</div>}>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={12}>
            <SimpleSelectAsync
              name="cnae"
              label="cnae"
              placeholder="cnae_code"
              onFetch={handleCnaeFetch}
              onFetchByValue={handleCnaeFetchByValue}
              emptyText="no_data"
              searchHintText="type_to_search"
            />
          </Col>

          <Col xs={24} sm={12}>
            <SimpleSelect
              name="lc116Code"
              label="lc116_code"
              allowClear
              showSearch
              options={lc116Options}
            />
          </Col>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6}>
            <SimpleNumber
              name="issTaxRate"
              label="iss_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>
        </Row>
      </Card>

      {/* TAX RETENTIONS CARD */}
      <Card title={
        <div className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {translateX('tax_retentions')}
          {crtConfig && (
            <Tag color={crtConfig.color} style={{ marginLeft: 4, fontSize: 11 }}>
              {crtConfig.label} - {crtConfig.description}
            </Tag>
          )}
        </div>
      }>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6}>
            <SimpleNumber
              name="pisTaxRate"
              label="pis_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>

          <Col xs={24} sm={6}>
            <SimpleNumber
              name="cofinsTaxRate"
              label="cofins_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>

          <Col xs={24} sm={6}>
            <SimpleNumber
              name="csllTaxRate"
              label="csll_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>

          <Col xs={24} sm={6}>
            <SimpleNumber
              name="irrfTaxRate"
              label="irrf_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6}>
            <SimpleNumber
              name="inssTaxRate"
              label="inss_rate"
              percentage
              min={0}
              max={100}
              decimalPlaces={2}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServicesFiscalTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildLc116Options(storedParameters, moduleId) {
  const lc116Array = storedParameters?.modules?.[moduleId]?.lc116;

  if (!lc116Array || !Array.isArray(lc116Array)) return [];

  return lc116Array.map(item => ({
    value: item.code,
    label: `${item.code} - ${item.description}`,
    text: `${item.code} - ${item.description}`,
  }));
}
